import React from 'react';
import { Link2 } from 'lucide-react';
import { buildGroupGantt } from './groupGantt';
import { plusOne } from './shiftSheetRules';

const TONE = {
  worker: 'bg-emerald-200 border-emerald-400 text-emerald-900',
  conflict: 'bg-red-200 border-red-500 text-red-900',
  open: 'bg-[repeating-linear-gradient(135deg,#fef3c7_0_6px,#fde68a_6px_12px)] border-amber-400 text-amber-900',
  neutral: 'bg-slate-200 border-slate-400 text-slate-800', accent: 'bg-violet-200 border-violet-400 text-violet-900', danger: 'bg-red-200 border-red-500 text-red-900',
};

/** UI-14 — member participant periods at their own times with that participant's coverage drawn over them; ratio strip below. */
export function GroupMiniGantt({ group, conflicts, onOpenMember }) {
  const { axis, rows, planned, actual } = buildGroupGantt(group, conflicts);
  const span = Math.max(1, axis.end - axis.start);
  const pct = (m) => `${Math.max(0, Math.min(100, ((m - axis.start) / span) * 100))}%`;
  const width = (s, e) => `${Math.max(1, ((Math.min(e, axis.end) - Math.max(s, axis.start)) / span) * 100)}%`;
  const Strip = ({ label, blocks, testId }) => (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="w-28 shrink-0 text-[10px] font-medium">{label}</span>
      <div className="relative flex-1 h-5 rounded bg-slate-50 border border-slate-200 overflow-hidden">
        {blocks.map((b, i) => <span key={i} className={`absolute inset-y-0 border rounded-sm text-[9px] px-1 flex items-center ${TONE[b.tone]}`} style={{ left: pct(b.start), width: width(b.start, b.end) }} title={`${plusOne(b.start)}–${plusOne(b.end)} ${b.label} · ${b.participants} participant(s) · ${b.workers} worker(s)`}>{b.label}</span>)}
      </div>
    </div>
  );
  return (
    <div className="rounded-lg border bg-card p-3 space-y-2" data-testid="group-mini-gantt">
      <div className="relative h-4 ml-28 text-[9px] text-muted-foreground">
        {axis.ticks.map((t) => <span key={t.at} className="absolute -translate-x-1/2 tabular-nums" style={{ left: pct(t.at) }}>{t.label}</span>)}
      </div>
      {rows.map((r) => (
        <div key={r.shift_id} className="flex items-start gap-2" data-testid={`gantt-row-${r.shift_id}`}>
          <button type="button" className="w-28 shrink-0 text-left text-[10px] leading-tight hover:underline text-sky-800" onClick={() => onOpenMember && onOpenMember(r.shift_id)} data-testid={`gantt-member-${r.shift_id}`}>
            <span className="font-medium truncate block">{r.label}</span><span className="text-muted-foreground tabular-nums">{r.period}</span>
          </button>
          <div className="relative flex-1 min-h-[1.25rem] rounded border border-slate-200 bg-slate-50" style={{ height: `${Math.max(1, r.lanes.length) * 1.25 + 0.25}rem` }}>
            <span className="absolute inset-y-0 rounded-sm bg-sky-100/80 border border-sky-300 flex items-center gap-0.5 px-1 text-[9px] text-sky-900" style={{ left: pct(r.start), width: width(r.start, r.end) }} title={`Participant period ${r.period} — intentional shared delivery`}><Link2 className="w-2.5 h-2.5" /></span>
            {r.open.map((b, i) => <span key={`o${i}`} className={`absolute top-0.5 h-4 border rounded-sm text-[9px] px-1 flex items-center ${TONE.open}`} style={{ left: pct(b.start), width: width(b.start, b.end) }} title={`Open ${plusOne(b.start)}–${plusOne(b.end)}`} data-testid={`gantt-open-${r.shift_id}`}>Open</span>)}
            {r.lanes.map((lane, li) => lane.blocks.map((b, bi) => (
              <span key={`${li}-${bi}`} className={`absolute h-4 border rounded-sm text-[9px] px-1 flex items-center truncate ${TONE[b.kind]}`} style={{ left: pct(b.start), width: width(b.start, b.end), top: `${li * 1.25 + 0.125}rem` }}
                title={lane.conflict ? `${lane.label} — ${lane.conflict.reason}` : `${lane.label} ${plusOne(b.start)}–${plusOne(b.end)}`} data-testid={`gantt-worker-${r.shift_id}-${lane.worker_id}`}>
                {lane.label}{b.label ? ` (${b.label})` : ''}
              </span>
            )))}
          </div>
        </div>
      ))}
      <Strip label="Ratio · planned" blocks={planned} testId="gantt-ratio-planned" />
      {actual.length > 0 && <Strip label="Ratio · actual" blocks={actual} testId="gantt-ratio-actual" />}
    </div>
  );
}

export default GroupMiniGantt;
